/**
 * The plan document store: `{values, manual}` plus everything @avia/core derives
 * from it.
 *
 * Rules, inherited verbatim from the single-file predecessor:
 *   · Typing into a field marks it MANUAL; clearing it un-marks it.
 *   · recompute() writes derived values back into every NON-manual auto id, and
 *     never touches the field the pilot is currently editing (it is refilled on blur).
 *   · Derived values are shown in petrol (`auto`) and can be overridden at any time.
 *   · A manually entered total (P14/P18), Mindest-Kraftstoff or Grundmasse flows
 *     through into everything downstream, exactly as on paper.
 *
 * Seven sources write into the form and the store keeps them apart:
 *   the PILOT (`values`/`manual`), the MAP's geometry (`route`, marked
 *   `routeMark`), the AIRCRAFT PROFILE (`setAircraft`), the WEATHER (`setWx`),
 *   the MAP's DATA (`setGeo` — what openAIP publishes about the places the route
 *   touches), the WINDS ALOFT (`setWinds` — the forecast at each leg's midpoint)
 *   and the TERRAIN (`setTerrain` — the DEM under the route: the suggested
 *   Sicherheitshöhe, and the aerodrome elevation openAIP did not publish; the
 *   last five are marked `derivedSource`). The route owns its cells even after an
 *   edit ("differs from map"); the other five lose a cell the moment the pilot
 *   types in it, and get it back only when the cell is cleared. Which cells those
 *   five currently own is persisted in `plans.meta`.
 *
 * The store is framework-free: React binds to it through useSyncExternalStore
 * with a per-field subscription, so a keystroke re-renders one input, not 400.
 */

import {
  airfield,
  computeFuel,
  computeLeg,
  computeMass,
  computeRoute,
  eta as computeEta,
  fmtHM,
  fmtWca,
  isLbaField,
  num,
  parseHM,
  safetyWarnings,
  sunTimes,
  type LegInput,
  type MassRow,
  type WarningLevel,
} from '@avia/core';

import { aircraftFuelValues, aircraftStaticValues, isEstimated, type AircraftFeed } from './aircraft';
import { LEG_NUMBERS, legField, parseDateParts, type LegNumber } from './fields';
import { parseRoute, routeSyncValues, type RouteDoc } from './route';

export type FieldFlag = '' | 'warn' | 'warn2';

/** The derived cells whose value legitimately drifts with the plan's date (see routeMark). */
const VAR_FIELD = /^leg\d+_var$/;

/**
 * A warning as the UI carries it: the stable code plus its substitutions. The
 * TEXT is not stored — it is looked up per language at render time (i18n's
 * `warnText`), with core's German message kept as the fallback for a code the
 * catalogue does not know yet.
 */
export interface WarnRef {
  code: string;
  params?: Record<string, string>;
  fallback?: string;
}

export interface UiWarning extends WarnRef {
  level: WarningLevel;
}

/**
 * What the weather briefing behind the ATIS rows was, small enough to travel in
 * `plans.meta`: enough to print the status line ("METAR LJLJ 10:20Z · TAF gültig
 * bis 18Z · Quelle …") on a plan nobody may fetch for — the shared read-only
 * view. The observations themselves are NOT stored: what the pilot briefed on is
 * in the form's own cells, and a cached METAR that looks live is a hazard.
 */
export interface WxStationStatus {
  id: string;
  /** ISO-8601 as the API gave it, e.g. `2026-08-17T10:20:00Z`; absent when the station had none */
  obs_time?: string;
  taf_valid_to?: string;
}

/**
 * What the nearby lookup found for a plan station NOAA publishes nothing for.
 *
 * It is stored for the reason the rest of this blob is: the **shared read-only view**
 * never fetches, and "kein METAR für LJCE" printed with no answer beside it is a
 * question rather than a briefing. `for` is the station that is silent and `id` the
 * neighbour that is not — the two may never be collapsed into one field, because the
 * whole hazard this fallback exists to avoid is a neighbour's report read as the
 * aerodrome's own.
 */
export interface WxNearbyStatus {
  /** the plan station with no report of its own */
  for: string;
  /** the neighbour that has one */
  id: string;
  /** openAIP's name for it, when the mirror had a row */
  name?: string;
  dist_nm: number;
  /** true bearing from `for` to `id`, 0 = north */
  bearing_deg: number;
  obs_time?: string;
  taf_valid_to?: string;
}

export interface WxStatus {
  fetched_at: string;
  /** the endpoint's own attribution string — stored, so a shared plan cites what it used */
  source?: string;
  stations: WxStationStatus[];
  /** what the nearby fallback found; absent on a plan where every station reports */
  nearby?: WxNearbyStatus[];
}

/**
 * What the winds-aloft fill behind the route's W/V column was, small enough to
 * travel in `plans.meta`: which model, for which hour, loaded when — and the
 * fingerprint of the legs it was made for.
 *
 * All four have to be stored, for three different reasons. `source` is the
 * licence (Open-Meteo is CC BY 4.0 and the ICON data is DWD's, so the string has
 * to be renderable on a plan nobody may fetch for — the shared read-only view).
 * `model`/`hour`/`fetched_at` are what dates the answer, and a forecast with no
 * time on it is worse than none. `fp` is what lets the map say **veraltet** on a
 * leg whose midpoint has moved since: a pilot can edit the route while a request
 * is out, so without it a redrawn leg would go on showing last route's wind with
 * nothing to distinguish it from a fresh one.
 */
export interface WindsStatus {
  fetched_at: string;
  /** `icon_d2` | `icon_eu` | `best_match` — the model that served the most legs */
  model?: string;
  /** the time asked for (the departure leg's), ISO-8601 */
  at?: string;
  /** the forecast hour actually served, e.g. `2026-08-17T12:00Z` — the FIRST one */
  hour?: string;
  /**
   * Every forecast hour the fetch used, in order, when it used more than one.
   *
   * A fetch may span up to `MAX_WIND_REQUESTS` hours precisely so a later leg
   * gets a later forecast, and `hour` alone is then a claim about leg 1 that the
   * per-cell tooltip would repeat on leg 8. Absent on the ordinary one-hour
   * fetch, so nothing grows in `plans.meta` for the common case.
   */
  hours?: string[];
  /** the endpoint's own attribution string — REQUIRED by the licence, so never optional in practice */
  source?: string;
  /** `1:46.223,14.458,3000;2:…` — where each leg's midpoint was, and at what altitude */
  fp?: string;
}

/**
 * The corridor a terrain profile is read over, in NM — the default, and the one a
 * stored plan is assumed to have been read at when it does not say.
 *
 * It lives HERE rather than in `terrain.ts` for the reason `MODEL_LABEL` does:
 * the field tooltip is produced by the store (`derivedLabel`), and the store must
 * not import the module that does the fetching (`terrain.ts` → `api.ts` →
 * `store.ts` would be a real cycle). `terrain.ts` imports it from here.
 */
export const DEFAULT_CORRIDOR_NM = 5;

/**
 * What one leg's terrain profile said, small enough to travel in `plans.meta`.
 *
 * `leg` is the **form's** leg number (1-based), like the winds fingerprint's — the
 * endpoint counts legs from 0 and the paper form counts them from 1, and only one
 * of the two may reach storage.
 */
export interface TerrainLegSummary {
  leg: number;
  max_terrain_ft?: number;
  msa_ft?: number;
  /** `flat` | `mountain` */
  msa_rule?: string;
}

/**
 * What the terrain profile behind the SafeAlt column was: how wide a corridor,
 * read when, and what it said per leg.
 *
 * It is stored for the same reason `wx` and `winds` are: the **shared read-only
 * view** never fetches, and a Sicherheitshöhe with no terrain figure behind it is
 * a number nobody can check. `source` is the attribution (Mapterhorn's terms and
 * openAIP's CC BY-NC), which has to be renderable on a page that may not fetch.
 * There is no `fetched_at` in the endpoint's answer — this one is the client's own
 * stamp, and it moves only when the summary itself changes (see `setTerrain`).
 */
export interface TerrainStatus {
  fetched_at: string;
  /** the corridor the profile was read over, NM */
  corridor_nm?: number;
  /** the endpoint's own attribution string — REQUIRED by the licence */
  source?: string;
  legs: TerrainLegSummary[];
}

/**
 * `plans.meta` (migration 0005): everything about a plan that is neither an LBA
 * field value nor a waypoint — which cells a non-pilot source filled, and the
 * weather briefing's provenance. The column is deliberately open: unknown keys
 * are carried back to the server untouched, exactly as `plans.rs` does inside
 * `route`.
 */
export interface PlanMeta {
  /** field ids currently owned by a derived source, by source name */
  derived?: {
    aircraft?: string[]; wx?: string[]; geo?: string[]; winds?: string[]; terrain?: string[];
  } & Record<string, unknown>;
  /** the METAR/TAF briefing behind the ATIS rows (see {@link WxStatus}) */
  wx?: WxStatus;
  /** the winds-aloft fill behind the W/V column (see {@link WindsStatus}) */
  winds?: WindsStatus;
  /** the terrain profile behind the SafeAlt column (see {@link TerrainStatus}) */
  terrain?: TerrainStatus;
  [key: string]: unknown;
}

/** A `meta.wx` blob from the API/localStorage, reduced to what this app understands. */
export function parseWxStatus(raw: unknown): WxStatus | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const o = raw as { fetched_at?: unknown; source?: unknown; stations?: unknown; nearby?: unknown };
  if (typeof o.fetched_at !== 'string') return null;
  const stations: WxStationStatus[] = [];
  if (Array.isArray(o.stations)) {
    for (const s of o.stations) {
      if (typeof s !== 'object' || s === null) continue;
      const r = s as { id?: unknown; obs_time?: unknown; taf_valid_to?: unknown };
      if (typeof r.id !== 'string' || !r.id) continue;
      stations.push({
        id: r.id,
        ...(typeof r.obs_time === 'string' ? { obs_time: r.obs_time } : {}),
        ...(typeof r.taf_valid_to === 'string' ? { taf_valid_to: r.taf_valid_to } : {}),
      });
    }
  }
  const nearby: WxNearbyStatus[] = [];
  // A plan written before the fallback existed simply has no `nearby` key, and one
  // written by a newer client may have entries this build cannot read — both are the
  // same rule as `stations` above: take what parses, drop what does not, never throw.
  if (Array.isArray(o.nearby)) {
    for (const n of o.nearby) {
      if (typeof n !== 'object' || n === null) continue;
      const r = n as Record<string, unknown>;
      if (typeof r['for'] !== 'string' || !r['for']) continue;
      if (typeof r['id'] !== 'string' || !r['id']) continue;
      if (typeof r['dist_nm'] !== 'number' || !Number.isFinite(r['dist_nm'])) continue;
      nearby.push({
        for: r['for'],
        id: r['id'],
        ...(typeof r['name'] === 'string' && r['name'] ? { name: r['name'] } : {}),
        dist_nm: r['dist_nm'],
        bearing_deg: typeof r['bearing_deg'] === 'number' && Number.isFinite(r['bearing_deg'])
          ? r['bearing_deg'] : 0,
        ...(typeof r['obs_time'] === 'string' ? { obs_time: r['obs_time'] } : {}),
        ...(typeof r['taf_valid_to'] === 'string' ? { taf_valid_to: r['taf_valid_to'] } : {}),
      });
    }
  }
  return {
    fetched_at: o.fetched_at,
    ...(typeof o.source === 'string' ? { source: o.source } : {}),
    stations,
    ...(nearby.length ? { nearby } : {}),
  };
}

/** A `meta.winds` blob from the API/localStorage, reduced to what this app understands. */
export function parseWindsStatus(raw: unknown): WindsStatus | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const o = raw as {
    fetched_at?: unknown; model?: unknown; at?: unknown; hour?: unknown; hours?: unknown;
    source?: unknown; fp?: unknown;
  };
  if (typeof o.fetched_at !== 'string') return null;
  const hours = Array.isArray(o.hours) ? o.hours.filter((h): h is string => typeof h === 'string') : [];
  return {
    fetched_at: o.fetched_at,
    ...(typeof o.model === 'string' ? { model: o.model } : {}),
    ...(typeof o.at === 'string' ? { at: o.at } : {}),
    ...(typeof o.hour === 'string' ? { hour: o.hour } : {}),
    ...(hours.length > 1 ? { hours } : {}),
    ...(typeof o.source === 'string' ? { source: o.source } : {}),
    ...(typeof o.fp === 'string' ? { fp: o.fp } : {}),
  };
}

/** A `meta.terrain` blob from the API/localStorage, reduced to what this app understands. */
export function parseTerrainStatus(raw: unknown): TerrainStatus | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const o = raw as { fetched_at?: unknown; corridor_nm?: unknown; source?: unknown; legs?: unknown };
  if (typeof o.fetched_at !== 'string') return null;
  const legs: TerrainLegSummary[] = [];
  if (Array.isArray(o.legs)) {
    for (const l of o.legs) {
      if (typeof l !== 'object' || l === null) continue;
      const r = l as { leg?: unknown; max_terrain_ft?: unknown; msa_ft?: unknown; msa_rule?: unknown };
      if (typeof r.leg !== 'number' || !Number.isInteger(r.leg)) continue;
      legs.push({
        leg: r.leg,
        ...(typeof r.max_terrain_ft === 'number' ? { max_terrain_ft: r.max_terrain_ft } : {}),
        ...(typeof r.msa_ft === 'number' ? { msa_ft: r.msa_ft } : {}),
        ...(typeof r.msa_rule === 'string' ? { msa_rule: r.msa_rule } : {}),
      });
    }
  }
  return {
    fetched_at: o.fetched_at,
    ...(typeof o.corridor_nm === 'number' ? { corridor_nm: o.corridor_nm } : {}),
    ...(typeof o.source === 'string' ? { source: o.source } : {}),
    legs,
  };
}

/** What gets stored and synced: the values, which of them the pilot typed, the route, the tags. */
export interface PlanDoc {
  values: Record<string, string>;
  manual: string[];
  /** the map's half of the plan — `plans.route` (migration 0004); null when there is none */
  route?: RouteDoc | null;
  /** `plans.meta` (migration 0005); null when the plan has nothing to remember */
  meta?: PlanMeta | null;
}

/** A jsonb blob from the API/localStorage, reduced to what this app understands. */
export function parsePlanMeta(raw: unknown): PlanMeta | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  return raw as PlanMeta;
}

/**
 * How a field relates to the map's route:
 *   ''  — nothing to do with it
 *   's' — the map wrote this value and it still matches (dashed underline)
 *   'o' — the map owns the cell but the pilot changed it ("differs from map")
 */
export type RouteMark = '' | 's' | 'o';

/**
 * Sources other than the pilot that write into the form. The route has its own
 * mark (it can *differ*, because the map keeps owning a cell the pilot edited);
 * an aircraft profile cannot — a pilot edit takes the cell away from it for good,
 * so the tag is simply gone.
 *
 * `'aircraft-est'` is the same source at the profile's OTHER tier: the number
 * behind the cell is a type-typical estimate (or one nobody has checked yet)
 * rather than a figure ticked off against this airframe's POH or weighing
 * report. The plan must not present the two the same way — see `isEstimated`.
 *
 * `'wx'` is the METAR/TAF briefing (`setWx`): the same bargain as the aircraft —
 * a pilot edit takes the cell for good, clearing it hands it back.
 *
 * `'geo'` is what openAIP publishes about the places the route touches (`setGeo`,
 * see `geo.ts`) — frequencies, runway, elevation, the checkpoint remarks. Same
 * bargain again, plus one rule of its own: a cell another source already filled
 * is left alone, so the first source to answer keeps the cell.
 *
 * `'winds'` is the forecast wind at each leg's midpoint (`setWinds`, see
 * `winds.ts`) — the route's W/V column and nothing else. Same bargain as the
 * weather: it is fetched unattended whenever the route or the departure hour
 * moves, a pilot edit takes the cell for good, clearing it hands it back.
 *
 * `'terrain'` is the DEM under the route (`setTerrain`, see `terrain.ts`): the
 * suggested Sicherheitshöhe per leg, and — only where openAIP publishes none —
 * the aerodrome elevation. Same bargain again, with one ranking rule of its own:
 * the elevation is a FALLBACK, so `setGeo` may take that cell back when openAIP
 * turns out to have an answer after all (see `setGeo`).
 */
export type DerivedSource = '' | 'aircraft' | 'aircraft-est' | 'wx' | 'geo' | 'winds' | 'terrain';

/** The route rows an aircraft profile may write a TAS into, plus the ids it owns elsewhere. */
const AIRCRAFT_STATIC_IDS: readonly string[] = (() => {
  const ids = [
    'lfz_muster', 'lfz_kennz',
    'fuel_anlassen_l', 'fuel_reserve_l', 'fuel_vorrat_l', 'fuel_vorrat_zeit',
    'mb_leer_m', 'mb_leer_mom', 'mb_kna_m', 'mb_unit_masse', 'mb_unit_moment',
  ];
  for (const j of LEG_NUMBERS) ids.push(legField(j, 'tas'));
  ids.push('alt_tas');
  return ids.filter(isLbaField);
})();

/** The one fed cell that follows a COMPUTED value (the total flight time). */
const AIRCRAFT_FUEL_IDS: readonly string[] = ['fuel_reise_l'];

const AUTO_LEG_KEYS = ['wca', 'gs', 'mh', 'mc', 'time', 'rest_dist', 'rest_time'] as const;
const AUTO_ALT_KEYS = ['wca', 'gs', 'mh', 'mc', 'time'] as const;

/** Every field id recompute() may write. Mirrors the predecessor's AUTO_IDS. */
export const AUTO_IDS: string[] = (() => {
  const ids = [
    'total_dist', 'total_time', 'eta', 'ss', 'safe_time', 'fuel_safe_time', 'fuel_mindest_l', 'fuel_extra_l',
    'mb_grund_m', 'mb_grund_mom', 'mb_start_m', 'mb_start_mom',
  ];
  for (const j of LEG_NUMBERS) for (const k of AUTO_LEG_KEYS) ids.push('leg' + j + '_' + k);
  for (const k of AUTO_ALT_KEYS) ids.push('alt_' + k);
  return ids.filter(isLbaField);
})();

/**
 * The winds endpoint's model ids, as they are written on a chart.
 *
 * It lives HERE rather than in `winds.ts` because the field tooltip is produced
 * by the store (`derivedLabel`), and the store must not import the module that
 * does the fetching — `winds.ts` → `api.ts` → `store.ts` would be a real cycle.
 * `winds.ts` imports this one instead, so the name exists once.
 */
const MODEL_LABEL: Readonly<Record<string, string>> = {
  icon_d2: 'ICON-D2',
  icon_eu: 'ICON-EU',
  best_match: 'Best Match',
};

/** "icon_d2" → "ICON-D2"; anything the server starts reporting keeps its own id. */
export const modelLabel = (id: string | null | undefined): string =>
  id ? MODEL_LABEL[id] ?? id.replace(/_/g, '-').toUpperCase() : '';

/** An ISO-8601 instant as the hour a pilot reads on a forecast: "13Z". */
const zulu = (iso: string | undefined): string => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? String(d.getUTCHours()).padStart(2, '0') + 'Z' : '';
};

/**
 * Which forecast hour a winds fill is FOR, as it is printed — "13Z", or
 * "13Z–15Z" when the fetch spanned more than one.
 *
 * A fetch of up to `MAX_WIND_REQUESTS` hours gives its later legs a later
 * forecast, which is the point of it. Naming only the first hour on every cell
 * and on the status line made leg 8 claim the forecast leg 1 got, and made the
 * map's own per-leg list (which has each leg's real hour) disagree with the form
 * on the same screen. The span is the honest one-line answer for a line that
 * covers all of them.
 */
export const windsHourText = (w: Pick<WindsStatus, 'hour' | 'hours' | 'at'> | null | undefined): string => {
  if (!w) return '';
  const list = (w.hours ?? []).map(zulu).filter(Boolean);
  if (list.length > 1) {
    const first = list[0] ?? '';
    const last = list[list.length - 1] ?? '';
    return first === last ? first : first + '–' + last;
  }
  return zulu(w.hour ?? w.at);
};

/** Separator inside a field snapshot string. */
export const FIELD_SEP = '\u0001';
const trimmed = (v: string | undefined): string => (v ?? '').trim();

export class PlanStore {
  values: Record<string, string> = {};
  manual = new Set<string>();
  /** ids currently displaying a computed value (rendered petrol) */
  auto = new Set<string>();
  flags = new Map<string, FieldFlag>();
  /** per-field warning (drives the tooltip) — a code, not a translated string */
  warns = new Map<string, WarnRef>();
  warnings: UiWarning[] = [];
  /** at least one warning is red — the status strip switches colour */
  red = false;
  autocalc = true;
  /** id of the field with focus; recompute() must not rewrite it under the caret */
  activeId: string | null = null;
  /** bumped whenever values/manual change — the autosave effect watches this */
  docRev = 0;

  /** the waypoint list the map edits; null when the plan has no route */
  route: RouteDoc | null = null;
  /** field id → the value the CURRENT route derives for it (see {@link routeMark}) */
  private routeValues = new Map<string, string>();
  /** legs the route has beyond the twelve the paper form can hold */
  droppedLegs = 0;

  /** the aircraft the plan is flown with; null when it has none (nothing is fed then) */
  private aircraft: AircraftFeed | null = null;
  /** cheap identity of {@link aircraft} — switching to the same one must not dirty the plan */
  private aircraftSig = '';
  /** ids currently holding a value the aircraft profile wrote (persisted in `plans.meta`) */
  private aircraftIds = new Set<string>();

  /** ids currently holding a value the METAR/TAF briefing wrote (persisted in `plans.meta`) */
  private wxIds = new Set<string>();
  /** ids currently holding a value the openAIP feed wrote (persisted in `plans.meta`) */
  private geoIds = new Set<string>();
  /** ids currently holding a value the winds-aloft fill wrote (persisted in `plans.meta`) */
  private windsIds = new Set<string>();
  /** ids currently holding a value the terrain profile wrote (persisted in `plans.meta`) */
  private terrainIds = new Set<string>();
  /** provenance of that briefing — the one thing about it the plan remembers */
  private wx: WxStatus | null = null;
  /** provenance of the winds-aloft fill (see {@link WindsStatus}) */
  private winds: WindsStatus | null = null;
  /** provenance of the terrain profile (see {@link TerrainStatus}) */
  private terrain: TerrainStatus | null = null;
  /** keys of `plans.meta` this client does not know — round-tripped, never dropped */
  private metaExtra: Record<string, unknown> = {};
  private derivedExtra: Record<string, unknown> = {};

  private fieldSubs = new Map<string, Set<() => void>>();
  private globalSubs = new Set<() => void>();
  private lastState = new Map<string, string>();

  /* ---------- reading ---------- */

  val(id: string): string {
    return trimmed(this.values[id]);
  }

  /** Non-empty LBA values only — what goes to the PDF, the API and localStorage. */
  collect(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [id, raw] of Object.entries(this.values)) {
      const v = trimmed(raw);
      if (v && isLbaField(id)) out[id] = v;
    }
    return out;
  }

  getDoc(): PlanDoc {
    return {
      values: this.collect(),
      manual: [...this.manual].filter(isLbaField).sort(),
      route: this.route,
      meta: this.getMeta(),
    };
  }

  /**
   * The plan's `meta` document: the derived-tag sets plus whatever else was in
   * the column. `null` when there is nothing to say — that clears the column
   * instead of storing an empty husk.
   */
  getMeta(): PlanMeta | null {
    const aircraft = [...this.aircraftIds].filter(isLbaField).sort();
    const wxIds = [...this.wxIds].filter(isLbaField).sort();
    const geoIds = [...this.geoIds].filter(isLbaField).sort();
    const windsIds = [...this.windsIds].filter(isLbaField).sort();
    const terrainIds = [...this.terrainIds].filter(isLbaField).sort();
    const derived: Record<string, unknown> = { ...this.derivedExtra };
    if (aircraft.length) derived['aircraft'] = aircraft;
    if (wxIds.length) derived['wx'] = wxIds;
    if (geoIds.length) derived['geo'] = geoIds;
    if (windsIds.length) derived['winds'] = windsIds;
    if (terrainIds.length) derived['terrain'] = terrainIds;
    const meta: Record<string, unknown> = { ...this.metaExtra };
    if (Object.keys(derived).length) meta['derived'] = derived;
    if (this.wx) meta['wx'] = this.wx;
    if (this.winds) meta['winds'] = this.winds;
    if (this.terrain) meta['terrain'] = this.terrain;
    return Object.keys(meta).length ? (meta as PlanMeta) : null;
  }

  /** The provenance of the plan's METAR/TAF briefing, or null when it has none. */
  wxStatus(): WxStatus | null {
    return this.wx;
  }

  /** The provenance of the plan's winds-aloft fill, or null when it has none. */
  windsStatus(): WindsStatus | null {
    return this.winds;
  }

  /** What the terrain profile said, or null when none has been read for this plan. */
  terrainStatus(): TerrainStatus | null {
    return this.terrain;
  }

  isEmpty(): boolean {
    return Object.keys(this.collect()).length === 0 && !this.route?.waypoints.length;
  }

  /** '', 's' (route-derived) or 'o' (route-derived but overridden in the form). */
  routeMark = (id: string): RouteMark => {
    const derived = this.routeValues.get(id);
    if (derived === undefined) return '';
    const cur = this.val(id);
    if (cur === derived.trim()) return 's';
    // VAR is the one derived cell that changes without anybody touching the route: it is
    // recomputed at the plan's Datum, and the WMM drifts. Measured over Europe, the
    // printed whole-degree value moves for 7 % of positions over six months and 16 % over
    // a year — so a plan opened months later, or a template instantiated with a fresh
    // date, would flag untouched cells as "edited by hand". One degree of drift is still
    // the map's number.
    if (VAR_FIELD.test(id)) {
      const a = Number(cur);
      const b = Number(derived.trim());
      if (cur !== '' && Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= 1) return 's';
    }
    return 'o';
  };

  /** Ids the route currently owns — what the map would overwrite on its next change. */
  routeFieldIds(): string[] {
    return [...this.routeValues.keys()];
  }

  /**
   * '' or 'aircraft'/'aircraft-est' — which non-pilot source filled this cell, and at
   * which tier (see {@link DerivedSource}). With no profile loaded yet the tier is
   * unknowable, so a tagged cell reads as the plain 'aircraft' mark.
   */
  derivedSource = (id: string): DerivedSource => {
    if (this.wxIds.has(id)) return 'wx';
    if (this.geoIds.has(id)) return 'geo';
    if (this.windsIds.has(id)) return 'winds';
    if (this.terrainIds.has(id)) return 'terrain';
    if (!this.aircraftIds.has(id)) return '';
    return this.aircraft && isEstimated(this.aircraft.profile, id) ? 'aircraft-est' : 'aircraft';
  };

  /**
   * What to name that source in the tooltip: the registration of the aircraft, or
   * the station a weather cell was read from.
   *
   * The station is READ BACK OUT of the document rather than stored beside the
   * tag: the ATIS row carries it in its own `station` cell and a Wetter-Beratung
   * line begins with `TAF LJLJ …`, so a second copy in `plans.meta` could only
   * ever go stale against the value the pilot is looking at.
   */
  derivedLabel = (id: string): string => {
    if (this.wxIds.has(id)) return this.wxLabel(id);
    if (this.geoIds.has(id)) return this.geoLabel(id);
    if (this.windsIds.has(id)) return this.windsLabel();
    if (this.terrainIds.has(id)) return this.terrainLabel(id);
    return this.aircraftIds.has(id) ? this.aircraft?.registration ?? '' : '';
  };

  private wxLabel(id: string): string {
    const m = id.match(/^atis(\d)_/);
    if (m) return this.val('atis' + m[1] + '_station').trim().split(/\s+/)[0]?.toUpperCase() ?? '';
    const word = this.val(id).split(/\s+/)[1] ?? '';
    return /^[A-Z]{4}$/.test(word) ? word : '';
  }

  /**
   * Which place an openAIP-derived cell is about — read back OUT of the document,
   * for the same reason the weather's label is: the aerodrome is already in `von`
   * / `nach` / `alt_name` and a checkpoint's name is in its own cp cell, so a
   * second copy beside the tag could only ever go stale against the value the
   * pilot is looking at.
   */
  private geoLabel(id: string): string {
    const cell = (which: string): string => this.val(which).trim().toUpperCase();
    let m: RegExpMatchArray | null;
    if ((m = id.match(/^aip_(p1|p2f|p20)_/))) {
      return cell(m[1] === 'p1' ? 'von' : m[1] === 'p2f' ? 'nach' : 'alt_name');
    }
    if ((m = id.match(/^fq_(dep|dest)_/))) return cell(m[1] === 'dep' ? 'von' : 'nach');
    if ((m = id.match(/^atis(\d)_station$/))) return cell(m[1] === '1' ? 'von' : 'nach');
    if ((m = id.match(/^cp(\d+)_freq$/))) return this.val('cp' + (m[1] ?? '') + '_name');
    if ((m = id.match(/^leg(\d+)_remark$/))) return this.val('cp' + (Number(m[1] ?? 0) + 1) + '_name');
    return '';
  }

  /**
   * Which forecast a W/V cell came from — the HOUR it is for, "12Z", and nothing
   * else. The model that produced it is not a fact a pilot acts on and does not
   * belong in a cell's tooltip; it is on the sources page and nowhere else.
   *
   * The hour cannot be read back out of the document the way the weather's and
   * openAIP's labels are — a "314/3" in the W/V column names no hour. It comes
   * out of `meta.winds`, which is stored anyway because the forecast time has to
   * survive a reload and a share link.
   */
  private windsLabel(): string {
    const w = this.winds;
    return w ? windsHourText(w) : '';
  }

  /**
   * Which terrain answer a cell came from — and the label doubles as the
   * DISTINCTION between the source's two jobs, which the renderer needs and the
   * tag alone does not carry:
   *
   *   · a SafeAlt is read off a profile over a corridor, and the corridor is the
   *     one number that makes the suggestion checkable ("5 NM")
   *   · an aerodrome elevation is a single DEM point, has no corridor, and is
   *     only ever written where openAIP publishes nothing — so it says '' and the
   *     field renders the fallback sentence instead (see `Field.tsx`)
   */
  private terrainLabel(id: string): string {
    if (!/^leg\d+_safealt$/.test(id)) return '';
    return String(this.terrain?.corridor_nm ?? DEFAULT_CORRIDOR_NM) + ' NM';
  }

  /** Ids the aircraft profile currently owns. */
  aircraftFieldIds(): string[] {
    return [...this.aircraftIds];
  }

  /** Ids the METAR/TAF briefing currently owns. */
  wxFieldIds(): string[] {
    return [...this.wxIds];
  }

  /** Ids the openAIP feed currently owns. */
  geoFieldIds(): string[] {
    return [...this.geoIds];
  }

  /** Ids the winds-aloft fill currently owns. */
  windsFieldIds(): string[] {
    return [...this.windsIds];
  }

  /** Ids the terrain profile currently owns. */
  terrainFieldIds(): string[] {
    return [...this.terrainIds];
  }

  /** The registration of the aircraft the plan is flown with, or ''. */
  aircraftRegistration(): string {
    return this.aircraft?.registration ?? '';
  }

  /* ---------- subscriptions ---------- */

  subscribeField = (id: string) => (cb: () => void): (() => void) => {
    let set = this.fieldSubs.get(id);
    if (!set) {
      set = new Set();
      this.fieldSubs.set(id, set);
    }
    set.add(cb);
    this.lastState.set(id, this.fieldState(id));
    return () => {
      const s = this.fieldSubs.get(id);
      if (!s) return;
      s.delete(cb);
      if (s.size === 0) {
        this.fieldSubs.delete(id);
        this.lastState.delete(id);
      }
    };
  };

  subscribe = (cb: () => void): (() => void) => {
    this.globalSubs.add(cb);
    return () => this.globalSubs.delete(cb);
  };

  /**
   * Snapshot string for one field:
   * `flag SEP auto SEP routeMark SEP derivedSource SEP derivedLabel SEP warnCode SEP warnJson SEP value`.
   * Language-independent by construction — switching DE/EN re-renders the labels
   * without touching the document.
   */
  fieldState = (id: string): string => {
    const flag = this.flags.get(id) ?? '';
    const isAuto = this.auto.has(id) && !this.manual.has(id) ? '1' : '0';
    const warn = this.warns.get(id);
    const code = warn?.code ?? '';
    const rest = warn ? JSON.stringify({ params: warn.params, fallback: warn.fallback }) : '';
    return (
      flag + FIELD_SEP + isAuto + FIELD_SEP + this.routeMark(id) + FIELD_SEP +
      this.derivedSource(id) + FIELD_SEP + this.derivedLabel(id) + FIELD_SEP +
      code + FIELD_SEP + rest + FIELD_SEP + (this.values[id] ?? '')
    );
  };

  getRev = (): number => this.docRev;

  private notify(): void {
    for (const [id, subs] of this.fieldSubs) {
      const next = this.fieldState(id);
      if (this.lastState.get(id) === next) continue;
      this.lastState.set(id, next);
      for (const cb of subs) cb();
    }
    for (const cb of this.globalSubs) cb();
  }

  /* ---------- writing ---------- */

  /**
   * The pilot typed: the value becomes manual (or stops being manual when cleared).
   *
   * The aircraft tag goes with it — a cell the pilot touched belongs to the pilot.
   * Clearing it hands the cell back (recompute's feed refills it on the spot), the
   * same bargain the petrol auto-values already offer.
   */
  set(id: string, value: string): void {
    this.values[id] = value;
    if (value.trim() === '') this.manual.delete(id);
    else this.manual.add(id);
    this.auto.delete(id);
    this.aircraftIds.delete(id);
    this.wxIds.delete(id);
    this.geoIds.delete(id);
    this.windsIds.delete(id);
    this.terrainIds.delete(id);
    this.recompute();
  }

  /** A checkbox ('X' or ''). Checkboxes are never auto-computed. */
  setChecked(id: string, checked: boolean): void {
    this.values[id] = checked ? 'X' : '';
    this.recompute();
  }

  /** The app filled a field on the pilot's behalf (the map's info card): sticks like typing. */
  userSet(id: string, value: string): void {
    this.values[id] = value;
    if (trimmed(value) === '') this.manual.delete(id);
    else this.manual.add(id);
    this.auto.delete(id);
    this.aircraftIds.delete(id);
    this.wxIds.delete(id);
    this.geoIds.delete(id);
    this.windsIds.delete(id);
    this.terrainIds.delete(id);
  }

  setActive(id: string | null): void {
    this.activeId = id;
  }

  setAutocalc(on: boolean): void {
    this.autocalc = on;
    this.recompute();
  }

  /** Replace the whole document (loading a plan from the API or localStorage). */
  loadDoc(doc: PlanDoc): void {
    this.values = {};
    for (const [id, v] of Object.entries(doc.values)) this.values[id] = String(v ?? '');
    this.manual = new Set(doc.manual);
    this.auto.clear();
    this.flags.clear();
    this.warns.clear();
    // the tags first: they decide which cells the aircraft feed may still write
    this.loadMeta(doc.meta ?? null);
    // apply:false — the stored values ARE the pilot's document; the route only says
    // which of them it owns, so an override the pilot made survives the reload.
    this.setRoute(doc.route ?? null, false);
  }

  /* ---------- the map's half ---------- */

  /**
   * The date the variation is taken at: the plan's own Datum when it parses,
   * otherwise today. WMM drift is ~0.1°/year, so "today" is stable enough that
   * a reload does not flip a rounded VAR.
   */
  private routeDate(): Date {
    const p = parseDateParts(this.val('datum'));
    if (p) {
      const d = new Date(Date.UTC(p.y, p.mo - 1, p.d));
      if (!Number.isNaN(d.getTime())) return d;
    }
    return new Date();
  }

  /**
   * Install a route.
   *
   * `apply` is the whole contract between the map and the form: the MAP owns the
   * derived cells, so any map edit (`apply: true`) overwrites them — including a
   * value the pilot had changed. Loading a document (`apply: false`) writes
   * nothing; it only recomputes what the route *would* say, which is what turns
   * a diverging cell into the "differs from map" hint.
   *
   * Written cells are marked MANUAL on purpose: rwK/NM/VAR are *inputs* to the
   * wind triangle, not outputs of it, so the calculator must treat them as given.
   */
  setRoute(route: RouteDoc | null, apply: boolean): void {
    const next = routeSyncValues(route, this.routeDate());
    const previous = this.routeValues;
    const owned = new Set<string>([...this.routeValues.keys(), ...(route?.synced_ids ?? [])]);
    this.routeValues = next.values;
    this.droppedLegs = next.dropped;

    if (apply) {
      for (const [id, v] of next.values) {
        if (!isLbaField(id)) continue;
        // Only cells the edit actually changed are rewritten. The map owns these cells and
        // takes them back whenever its own answer moves — but renaming a waypoint, or
        // adding a leg the paper form cannot reach, changes no course and no distance, and
        // silently throwing away the pilot's hand-entered value for those is a data loss
        // with no cause. `previous` is what the route said before this edit, so a first
        // application (empty map) still writes everything.
        if (previous.get(id) === v) continue;
        this.values[id] = v;
        if (v.trim() === '') this.manual.delete(id);
        else this.manual.add(id);
        this.auto.delete(id);
      }
      // a route that lost legs must not leave last week's numbers in rows 5–8
      for (const id of owned) {
        if (next.values.has(id)) continue;
        this.values[id] = '';
        this.manual.delete(id);
        this.auto.delete(id);
      }
    }

    this.route = route ? { ...route, synced_ids: [...next.values.keys()] } : null;
    this.recompute();
  }

  /** Replace the waypoint list from the map (always overwrites the derived cells). */
  setWaypoints(waypoints: RouteDoc['waypoints']): void {
    this.setRoute({ v: 1, waypoints }, true);
  }

  /* ---------- the aircraft's half ---------- */

  /** Read `plans.meta`: the tag sets this client knows, everything else kept for the round trip. */
  private loadMeta(meta: PlanMeta | null): void {
    this.aircraftIds = new Set();
    this.wxIds = new Set();
    this.geoIds = new Set();
    this.windsIds = new Set();
    this.terrainIds = new Set();
    this.wx = null;
    this.winds = null;
    this.terrain = null;
    this.metaExtra = {};
    this.derivedExtra = {};
    if (!meta) return;
    for (const [k, v] of Object.entries(meta)) {
      if (k !== 'derived' && k !== 'wx' && k !== 'winds' && k !== 'terrain') this.metaExtra[k] = v;
    }
    this.wx = parseWxStatus(meta.wx);
    this.winds = parseWindsStatus(meta.winds);
    this.terrain = parseTerrainStatus(meta.terrain);
    const derived = meta.derived;
    if (!derived || typeof derived !== 'object') return;
    for (const [k, v] of Object.entries(derived)) {
      if (k !== 'aircraft' && k !== 'wx' && k !== 'geo' && k !== 'winds' && k !== 'terrain') {
        this.derivedExtra[k] = v;
      }
    }
    const readIds = (raw: unknown, into: Set<string>): void => {
      if (!Array.isArray(raw)) return;
      for (const id of raw) if (typeof id === 'string' && isLbaField(id)) into.add(id);
    };
    readIds(derived.aircraft, this.aircraftIds);
    readIds(derived.wx, this.wxIds);
    readIds(derived.geo, this.geoIds);
    readIds(derived.winds, this.windsIds);
    readIds(derived.terrain, this.terrainIds);
  }

  /**
   * Attach (or detach) the aircraft the plan is flown with. The feed itself runs
   * inside {@link recompute}, so the profile is re-applied on every change of the
   * document — that is what makes a new leg get its TAS without anybody asking.
   *
   * Detaching leaves every value where it is: the plan may still be printed and
   * flown. It only stops the profile from writing anything further.
   */
  setAircraft(a: AircraftFeed | null): void {
    const sig = a ? JSON.stringify([a.id, a.registration, a.typeCode, a.profile]) : '';
    if (sig === this.aircraftSig) return;
    // captured BEFORE the tags are touched, so a real detach counts as an edit
    const before = this.docKey();
    const rev = this.docRev;
    this.aircraftSig = sig;
    this.aircraft = a;
    // Detaching hands every fed cell back to the pilot. The values stay — the plan is
    // still flyable and printable — but the tags cannot: a tag with no aircraft behind it
    // paints a dashed "derived" underline whose tooltip can name no source, and would go
    // on claiming those cells in `plans.meta` for good. The same state is reached with no
    // user action at all when the aircraft is deleted (`aircraft_id` is `on delete set
    // null`). Note this is only reachable from a real detach: `setAircraft(null)` with no
    // aircraft attached returns above, so a fresh `loadDoc` keeps the tags it just read.
    if (!a) this.aircraftIds.clear();
    this.recompute();
    // Attaching an aircraft that changes nothing is not an edit. Without this the
    // autosave would PUT an identical document (and burn a version) every time a
    // plan with an aircraft is opened.
    if (this.docKey() === before) this.docRev = rev;
  }

  /** Everything that makes a plan document, as one comparable string. */
  private docKey(): string {
    return (
      JSON.stringify(this.collect()) + FIELD_SEP + [...this.aircraftIds].sort().join(',') +
      FIELD_SEP + [...this.wxIds].sort().join(',') +
      FIELD_SEP + [...this.geoIds].sort().join(',') +
      FIELD_SEP + [...this.windsIds].sort().join(',') +
      FIELD_SEP + [...this.terrainIds].sort().join(',')
    );
  }

  /** Does this route row carry anything the form would print a TAS next to? */
  private rowFilled = (row: LegNumber | 'alt'): boolean => {
    if (this.val(legField(row, 'dist')) !== '' || this.val(legField(row, 'tc')) !== '') return true;
    // the checkpoint at the END of leg j is cp(j+1); the alternate has its own name cell
    return row === 'alt' ? this.val('alt_name') !== '' : this.val('cp' + (row + 1) + '_name') !== '';
  };

  /**
   * Write one stage of the aircraft feed.
   *
   * `domain` is every id this stage may own, so the two stages cannot clear each
   * other's cells. The rule is the whole contract:
   *   · an EMPTY cell or one still tagged aircraft-derived is (re)written
   *   · anything else is the pilot's and is left alone, for good
   *   · a tagged cell the profile no longer has a value for is emptied and untagged,
   *     the same way the map takes back a row its route no longer reaches
   */
  private applyFeed(next: Map<string, string>, domain: readonly string[]): void {
    if (!this.aircraft) return;
    for (const id of domain) {
      if (id === this.activeId) continue; // never rewrite the cell under the caret
      // "a pilot edit wins for good" is enforced in set()/userSet() by dropping the tag,
      // which makes the two sets disjoint by construction — but only for documents this
      // client wrote. `loadDoc` restores `manual` and the tags from two independent inputs
      // (`plan.manual`, `plan.meta`), so the rule is repeated here rather than assumed.
      if (this.manual.has(id)) { this.aircraftIds.delete(id); continue; }
      const v = next.get(id);
      const tagged = this.aircraftIds.has(id);
      if (v === undefined) {
        if (!tagged) continue;
        this.values[id] = '';
        this.manual.delete(id);
        this.aircraftIds.delete(id);
        continue;
      }
      if (!tagged && this.val(id) !== '') continue;
      this.values[id] = v;
      this.aircraftIds.add(id);
    }
  }

  /* ---------- the weather's half ---------- */

  /**
   * Write a METAR/TAF briefing into the form (the ATIS rows, the Wetter-Beratung
   * lines) and remember what it was.
   *
   * The contract is the aircraft profile's, cell for cell — and for the same
   * reason it is repeated rather than shared: `loadDoc` restores `manual` and the
   * tags from two independent inputs, so "a pilot edit wins for good" cannot be
   * assumed, it has to be checked.
   *   · an EMPTY cell or one still tagged wx-derived is (re)written
   *   · so is one the openAIP feed wrote, and ONLY that one. `atis1_station` is why:
   *     that feed names the station ("LJLJ ATIS 128.175") and this one reports what
   *     the weather is doing there, and a row carrying openAIP's name for LJMB and
   *     the briefing's observation of LJLJ is a hazard rather than a compromise.
   *     The ranking is one-directional — `setGeo` never takes a wx-owned cell back —
   *     so the two cannot trade the cell between them. It stays untouched in the
   *     ordinary case anyway, because `atisFill` declines to rewrite a station cell
   *     that already names this station, frequency and all.
   *   · anything else is the pilot's and is left alone, for good
   *   · an EMPTY value clears a tagged cell (this hour's METAR carries no QNH,
   *     so last hour's must not stay) and does nothing to an untouched one
   *
   * Unlike the aircraft feed this does NOT run inside recompute(): the values
   * come off the network, unattended — when the stations change, on the
   * ten-minute beat, and from the section's ⟳ (`wxContext.tsx`).
   */
  setWx(next: Map<string, string>, status?: WxStatus | null): void {
    let changed = false;
    for (const [id, raw] of next) {
      if (!isLbaField(id)) continue;
      if (id === this.activeId) continue; // never rewrite the cell under the caret
      if (this.manual.has(id)) {
        if (this.wxIds.delete(id)) changed = true;
        continue;
      }
      const v = raw.trim();
      const tagged = this.wxIds.has(id);
      if (v === '') {
        if (!tagged) continue;
        if (this.val(id) !== '') this.values[id] = '';
        this.wxIds.delete(id);
        changed = true;
        continue;
      }
      if (!tagged && this.val(id) !== '' && !this.geoIds.has(id)) continue;
      if (this.val(id) !== v) {
        this.values[id] = v;
        changed = true;
      }
      this.auto.delete(id);
      this.aircraftIds.delete(id);
      if (this.geoIds.delete(id)) changed = true;
      this.windsIds.delete(id);
      this.terrainIds.delete(id);
      if (!tagged) {
        this.wxIds.add(id);
        changed = true;
      }
    }
    // The briefing now arrives unattended, every ten minutes for as long as the
    // plan is open. A METAR that has not been reissued is the SAME briefing, and
    // stamping a new `fetched_at` on it would dirty the document — and autosave a
    // new version — for a request that learned nothing. See `setTerrain`.
    if (status !== undefined && wxKey(status) !== wxKey(this.wx)) {
      this.wx = status;
      changed = true;
    }
    if (!changed) return;
    this.recompute();
  }

  /* ---------- the map's OTHER half: what openAIP knows ---------- */

  /**
   * Write what openAIP publishes about the places the route touches (see
   * `geo.ts`): the V2 frequencies, the V7 aerodrome rows, the B6 station cells
   * and the per-checkpoint frequency/remark columns.
   *
   * `next` is the WHOLE domain the source may own (`GEO_IDS`), with `''` for
   * every cell this route has nothing to say about — that is what makes a deleted
   * waypoint take its remark with it, the same way the route sync takes back a cp
   * name it no longer reaches.
   *
   * The contract is the weather's, cell for cell, with one addition:
   *   · an EMPTY cell or one still tagged geo-derived is (re)written
   *   · anything else is left alone — the pilot's for good, and a value another
   *     source already put there stays that source's. A METAR's `atis1_station`
   *     is the case that matters: the briefing and this feed both have something
   *     to say about that cell, and whoever answered first keeps it.
   *   · a tagged cell this pass has no value for is emptied and untagged
   *   · the ONE exception to "first source keeps the cell" is a TERRAIN-owned
   *     one: the DEM writes `aip_p*_elev` only as a fallback for an aerodrome
   *     openAIP publishes no elevation for, so the published figure takes that
   *     cell back whenever it turns up — otherwise which of the two won would
   *     depend on which request answered first
   *
   * Unlike the aircraft feed this does NOT run inside recompute(): the values come
   * off the network, and a route edit is what asks for them. It also recomputes
   * only when something really moved — it runs unattended after every waypoint
   * change, and an unconditional bump would autosave a byte-identical document.
   */
  setGeo(next: Map<string, string>): void {
    let changed = false;
    for (const [id, raw] of next) {
      if (!isLbaField(id)) continue;
      if (id === this.activeId) continue; // never rewrite the cell under the caret
      if (this.manual.has(id)) {
        if (this.geoIds.delete(id)) changed = true;
        continue;
      }
      const v = raw.trim();
      const tagged = this.geoIds.has(id);
      if (v === '') {
        if (!tagged) continue;
        if (this.val(id) !== '') this.values[id] = '';
        this.geoIds.delete(id);
        changed = true;
        continue;
      }
      if (!tagged && this.val(id) !== '' && !this.terrainIds.has(id)) continue;
      if (this.val(id) !== v) {
        this.values[id] = v;
        changed = true;
      }
      this.auto.delete(id);
      this.aircraftIds.delete(id);
      this.wxIds.delete(id);
      this.windsIds.delete(id);
      if (this.terrainIds.delete(id)) changed = true;
      if (!tagged) {
        this.geoIds.add(id);
        changed = true;
      }
    }
    if (changed) this.recompute();
  }

  /* ---------- the winds aloft ---------- */

  /**
   * Write the forecast wind into the route's W/V column (see `winds.ts`).
   *
   * The contract is the weather's, cell for cell, and the three-way distinction
   * in `next` is what carries the rules the endpoint imposes:
   *   · a VALUE fills a cell that is empty or still tagged winds-derived
   *   · `''` clears such a cell — that is how a leg the route no longer has
   *     gives its wind back instead of feeding the triangle of a row that is gone
   *   · an ABSENT id is left exactly as it stands, which is what a leg the
   *     forecast could not answer for gets (`error: "unavailable"`): last hour's
   *     wind is still the best number on the form, and blanking it would be a
   *     silent downgrade
   *
   * Anything else is the pilot's, for good. Like `setWx` this does NOT run inside
   * recompute(): the values come off the network, unattended, whenever the route
   * or the departure hour moves (`windsContext.tsx`).
   * The cells are *inputs* to the wind triangle rather than outputs of it, so
   * writing them here is what makes WCA/GS/leg times follow in the same pass —
   * they are deliberately NOT marked manual, or clearing one would not hand it
   * back.
   *
   * Returns how many cells really changed, so the UI can say what happened.
   */
  setWinds(next: Map<string, string>, status?: WindsStatus | null): number {
    let written = 0;
    let changed = false;
    for (const [id, raw] of next) {
      if (!isLbaField(id)) continue;
      if (id === this.activeId) continue; // never rewrite the cell under the caret
      if (this.manual.has(id)) {
        if (this.windsIds.delete(id)) changed = true;
        continue;
      }
      const v = raw.trim();
      const tagged = this.windsIds.has(id);
      if (v === '') {
        if (!tagged) continue;
        if (this.val(id) !== '') written++;
        this.values[id] = '';
        this.windsIds.delete(id);
        changed = true;
        continue;
      }
      if (!tagged && this.val(id) !== '') continue;
      if (this.val(id) !== v) {
        this.values[id] = v;
        written++;
      }
      this.auto.delete(id);
      this.aircraftIds.delete(id);
      this.wxIds.delete(id);
      this.geoIds.delete(id);
      this.terrainIds.delete(id);
      if (!tagged) {
        this.windsIds.add(id);
        changed = true;
      }
    }
    // The forecast is re-read unattended after every route edit and whenever the
    // pilot comes back to an hour-old tab. The same model, the same hour and the
    // same legs is the same answer — replacing the status would move `fetched_at`
    // and autosave a plan nobody touched. See `setTerrain` for the same bargain.
    if (status !== undefined && windsKey(status) !== windsKey(this.winds)) {
      this.winds = status;
      changed = true;
    }
    if (written || changed) this.recompute();
    return written;
  }

  /* ---------- the terrain ---------- */

  /**
   * Write what the DEM under the route says (see `terrain.ts`): the suggested
   * Sicherheitshöhe in `leg<N>_safealt`, and — only where openAIP publishes none —
   * the aerodrome elevation in `aip_p1_elev` / `aip_p2f_elev`.
   *
   * The contract is `setGeo`'s, cell for cell: a cell that is EMPTY or still
   * tagged terrain-derived is (re)written, `''` clears such a cell (that is how a
   * leg the route no longer has gives its SafeAlt back), and anything else is left
   * alone — the pilot's for good, and another source's cell stays that source's.
   *
   * Two things it does NOT do. It does not mark the cells manual: SafeAlt is a
   * value the pilot must be able to hand back by clearing it, exactly like the
   * W/V column. And it does not run inside `recompute()` — the profile comes off
   * the network after a route change, and the SafeAlt column is written from
   * whatever it answered.
   *
   * `status` is the profile's own summary. It is replaced only when the SUMMARY
   * changed, not merely because it was fetched again: the profile is re-read
   * unattended after every route edit, and stamping a new `fetched_at` on an
   * identical answer would dirty a plan that nobody touched — including one just
   * opened, which would autosave a new version for reading it.
   *
   * Returns how many CELLS really changed — a count the UI puts in front of the
   * pilot ("2 Zellen übernommen"), so a tag that moved without its value moving
   * is a change to the document but not one to report.
   */
  setTerrain(next: Map<string, string>, status?: TerrainStatus | null): number {
    let written = 0;
    let changed = false;
    for (const [id, raw] of next) {
      if (!isLbaField(id)) continue;
      if (id === this.activeId) continue; // never rewrite the cell under the caret
      if (this.manual.has(id)) {
        if (this.terrainIds.delete(id)) changed = true;
        continue;
      }
      const v = raw.trim();
      const tagged = this.terrainIds.has(id);
      if (v === '') {
        if (!tagged) continue;
        if (this.val(id) !== '') {
          this.values[id] = '';
          written++;
        }
        this.terrainIds.delete(id);
        changed = true;
        continue;
      }
      if (!tagged && this.val(id) !== '') continue;
      if (this.val(id) !== v) {
        this.values[id] = v;
        written++;
      }
      this.auto.delete(id);
      this.aircraftIds.delete(id);
      this.wxIds.delete(id);
      this.geoIds.delete(id);
      this.windsIds.delete(id);
      if (!tagged) {
        this.terrainIds.add(id);
        changed = true;
      }
    }
    if (status !== undefined && terrainKey(status) !== terrainKey(this.terrain)) {
      this.terrain = status;
      changed = true;
    }
    if (written || changed) this.recompute();
    return written;
  }

  /**
   * Load a bare value map (imported JSON / example flight). Derived fields whose
   * stored value matches what auto-calc would produce become live again;
   * divergent stored values stay frozen as manual overrides.
   */
  loadValues(values: Record<string, string>, markManual: boolean): void {
    this.values = {};
    for (const [id, v] of Object.entries(values)) if (isLbaField(id)) this.values[id] = String(v ?? '');
    this.manual = new Set();
    this.auto.clear();
    this.flags.clear();
    this.warns.clear();
    // a bare value map replaces the whole document, so the old waypoints go too —
    // keeping them would leave a map that no longer describes what the form says
    this.route = null;
    this.routeValues = new Map();
    this.droppedLegs = 0;
    // …and so do the tags: every value in the new document is the file's, not the
    // aircraft's. The profile fills whatever the import left empty on the next pass.
    this.loadMeta(null);
    if (!markManual) {
      this.recompute();
      return;
    }
    for (const id of Object.keys(this.values)) if (this.val(id) !== '') this.manual.add(id);
    this.reconcileAuto();
  }

  private reconcileAuto(): void {
    if (!this.autocalc) {
      for (const id of AUTO_IDS) if (this.val(id) !== '') this.manual.add(id);
      this.recompute();
      return;
    }
    const stored: Record<string, string> = {};
    for (const id of AUTO_IDS) {
      stored[id] = this.val(id);
      this.manual.delete(id);
    }
    this.recompute();
    for (const id of AUTO_IDS) {
      const now = this.val(id);
      const was = stored[id] ?? '';
      if (was !== '' && now !== was) {
        this.values[id] = was;
        this.manual.add(id);
        this.auto.delete(id);
      }
    }
    this.recompute();
  }

  /** Write a computed value — unless the pilot overrode it or is editing it right now. */
  private setAuto(id: string, v: string | number | null | undefined): void {
    if (this.manual.has(id)) return;
    if (this.activeId === id) return;
    const s = v == null || (typeof v === 'number' && !Number.isFinite(v)) ? '' : String(v);
    if (this.values[id] !== s) this.values[id] = s;
    if (s !== '') this.auto.add(id);
    else this.auto.delete(id);
  }

  private clearFlags(): void {
    this.flags.clear();
    this.warns.clear();
    this.warnings = [];
    this.red = false;
  }

  private flag(id: string, f: FieldFlag, warn?: WarnRef): void {
    if (f) this.flags.set(id, f);
    if (warn) this.warns.set(id, warn);
  }

  /* ---------- the computation ---------- */

  /**
   * Where the flight ends, as far as the plan knows — for the sunset and nothing
   * else.
   *
   * The last waypoint first: a route drawn on the map ends exactly where the
   * flight does, whether or not its aerodrome has an ICAO code. `nach` (then
   * `von`, for a local flight written without a destination) is the fallback for
   * a plan filled in without the map, and resolves against the seed table only —
   * this is a pure function of the document, and a network lookup here would make
   * the SS cell wait on openAIP.
   */
  private endPoint(): { lat: number; lon: number } | null {
    const wps = this.route?.waypoints ?? [];
    const last = wps[wps.length - 1];
    if (last && Number.isFinite(last.lat) && Number.isFinite(last.lon)) {
      return { lat: last.lat, lon: last.lon };
    }
    for (const id of ['nach', 'von']) {
      const a = airfield(this.val(id));
      if (a) return { lat: a.lat, lon: a.lon };
    }
    return null;
  }

  /**
   * SS — sunset at the destination, in UTC, as an ordinary derived value.
   *
   * It used to need a button, which meant the two sunset warnings
   * (`eta-after-sunset`, `eta-near-sunset`) could not fire on a plan where
   * nobody had pressed it — a safety check that depended on a click. It is a
   * function of the date and the place, both of which are on the form, so it is
   * computed like every other derived cell: typing over it takes the cell for
   * good, clearing it hands it back.
   *
   * It runs before the autocalc branch on purpose. `ed.autocalc` is about the
   * navigation ARITHMETIC a pilot may want to do by hand; when the sun sets is
   * not arithmetic, and the warning that reads it has to work either way.
   */
  private applySunset(): void {
    const p = parseDateParts(this.val('datum'));
    const at = p ? this.endPoint() : null;
    if (!p || !at) {
      this.setAuto('ss', '');
      return;
    }
    const min = sunTimes(p.y, p.mo, p.d, at.lat, at.lon).sunsetMin;
    // null = polar day or night: there is no sunset to print
    this.setAuto('ss', min === null ? '' : fmtHM(min));
  }

  recompute(): void {
    this.docRev++;
    this.applySunset();
    // Stage 1 of the aircraft feed, BEFORE anything is read: TAS is an input to
    // the wind triangle, so a leg that just gained a distance has to have its TAS
    // before this same pass computes the leg — not one pass later.
    this.applyFeed(aircraftStaticValues(this.aircraft, this.rowFilled), AIRCRAFT_STATIC_IDS);
    if (!this.autocalc) {
      // hand-calculated: the total time is whatever the pilot wrote in P18
      this.applyFeed(aircraftFuelValues(this.aircraft, finite(num(this.val('total_time')))), AIRCRAFT_FUEL_IDS);
      this.clearFlags();
      this.notify();
      return;
    }
    this.clearFlags();

    const val = (id: string) => this.val(id);
    /** Only a value the pilot typed counts as an override for @avia/core. */
    const override = (id: string): string | undefined => (this.manual.has(id) ? val(id) : undefined);

    const legInput = (j: LegNumber | 'alt'): LegInput => {
      const li: LegInput = {
        tas: val(legField(j, 'tas')),
        wind: val(legField(j, 'wind')),
        tc: val(legField(j, 'tc')),
        var: val(legField(j, 'var')),
        dist: val(legField(j, 'dist')),
      };
      // optional keys are ABSENT rather than undefined (exactOptionalPropertyTypes)
      const wca = override(legField(j, 'wca')); if (wca !== undefined) li.wca = wca;
      const gs = override(legField(j, 'gs')); if (gs !== undefined) li.gs = gs;
      const time = override(legField(j, 'time')); if (time !== undefined) li.time = time;
      return li;
    };

    // Pass 1 gives the wind-triangle GS. The form shows GS rounded to whole knots
    // and derives the leg time from that rounded number, so pass 2 feeds the
    // rounded GS back in — the difference is a minute here and there, and the
    // printed form has to agree with itself.
    const inputs = LEG_NUMBERS.map(legInput);
    const pass1 = computeRoute(inputs);
    const inputs2 = inputs.map((li, i) => {
      const gs = pass1.legs[i]?.gs;
      return li.gs !== undefined || gs == null ? li : { ...li, gs: String(gs) };
    });

    // The Rest columns are counted back from the DISPLAYED (rounded) total — the
    // paper form's P14/P18 — not from the unrounded sum, so a route with fractional
    // NM ends on 0 in the last row exactly as it does on paper. A pilot-entered
    // total wins over the computed one.
    const base = computeRoute(inputs2);
    const manualDist = num(override('total_dist'));
    const manualTime = num(override('total_time'));
    const totalDistOverride = Number.isFinite(manualDist) ? manualDist : base.totalDistNm ?? undefined;
    const totalTimeOverride = Number.isFinite(manualTime) ? manualTime : base.totalTimeMin ?? undefined;
    const route = computeRoute(inputs2, {
      ...(totalDistOverride !== undefined ? { totalDistOverride } : {}),
      ...(totalTimeOverride !== undefined ? { totalTimeOverride } : {}),
    });

    // VAR is written once and carried down the rows; the alternate row inherits
    // the last one, but must never count towards the totals.
    const carriedVar = route.legs[route.legs.length - 1]?.variation ?? NaN;
    const altInput = legInput('alt');
    const altPass1 = computeLeg(altInput, carriedVar);
    const alt = computeLeg(
      altInput.gs !== undefined || altPass1.gs == null ? altInput : { ...altInput, gs: String(altPass1.gs) },
      carriedVar,
    );

    const unflyable: UiWarning[] = [];
    const writeRow = (j: LegNumber | 'alt', r: { wca: number | null; gs: number | null; mh: number | null; mc: number | null; timeMin: number | null; unflyable: boolean }) => {
      const id = (k: string) => legField(j, k);
      if (r.unflyable) {
        this.setAuto(id('wca'), '');
        this.setAuto(id('gs'), '');
        this.flag(id('wind'), 'warn', { code: 'wind-ge-tas' });
        // 'alt' stays the code the renderer translates ("Ausweich" / "Alternate")
        unflyable.push({ level: 'red', code: 'leg-unflyable', params: { leg: String(j) } });
      } else {
        this.setAuto(id('wca'), r.wca === null ? '' : fmtWca(r.wca));
        this.setAuto(id('gs'), r.gs);
      }
      this.setAuto(id('mh'), r.mh);
      this.setAuto(id('mc'), r.mc);
      this.setAuto(id('time'), r.timeMin);
    };

    route.legs.forEach((r, i) => {
      const j = LEG_NUMBERS[i];
      if (j !== undefined) writeRow(j, r);
    });
    writeRow('alt', alt);

    this.setAuto('total_dist', route.totalDistNm);
    this.setAuto('total_time', route.totalTimeMin);
    LEG_NUMBERS.forEach((j, i) => {
      this.setAuto('leg' + j + '_rest_dist', route.restDistNm[i] ?? null);
      this.setAuto('leg' + j + '_rest_time', route.restTimeMin[i] ?? null);
    });

    // ETA = ETD + the EFFECTIVE total time (a hand-written P18 wins)
    const totalTimeEff = num(val('total_time'));
    const etaMin = computeEta(val('etd'), Number.isFinite(totalTimeEff) ? Math.round(totalTimeEff) : null);
    this.setAuto('eta', etaMin === null ? '' : fmtHM(etaMin));

    // Stage 2 of the aircraft feed: the cruise burn over the flight time this pass
    // just produced. It is written HERE — after the total, before the fuel block —
    // so the same recompute that changed the total also feeds the litres into
    // computeFuel. No second pass, therefore no loop.
    this.applyFeed(aircraftFuelValues(this.aircraft, finite(totalTimeEff)), AIRCRAFT_FUEL_IDS);

    // fuel
    const fuel = computeFuel({
      reise: val('fuel_reise_l'),
      anlassen: val('fuel_anlassen_l'),
      steig: val('fuel_steig_l'),
      anab: val('fuel_anab_l'),
      altn: val('fuel_altn_l'),
      reserve: val('fuel_reserve_l'),
      ...(override('fuel_mindest_l') !== undefined ? { mindestOverride: val('fuel_mindest_l') } : {}),
      vorrat: val('fuel_vorrat_l'),
      vorratZeit: val('fuel_vorrat_zeit'),
    });
    this.setAuto('fuel_mindest_l', fuel.mindestL);
    this.setAuto('fuel_extra_l', fuel.extraL);
    this.setAuto('fuel_safe_time', fuel.safeTime ?? '');
    this.setAuto('safe_time', fuel.safeTime ?? '');

    // mass & balance
    const row = (key: string): MassRow => ({ m: val('mb_' + key + '_m'), mom: val('mb_' + key + '_mom') });
    const mass = computeMass({
      leer: row('leer'),
      kna: row('kna'),
      schmier: row('schmier'),
      grundOverride: {
        m: override('mb_grund_m') ?? '',
        mom: override('mb_grund_mom') ?? '',
      },
      sitz1: row('sitz1'),
      sitz2: row('sitz2'),
      sitz3: row('sitz3'),
      gepVorn: row('gep_vorn'),
      gepMitte: row('gep_mitte'),
      gepHinten: row('gep_hinten'),
      kausf: row('kausf'),
    });
    this.setAuto('mb_grund_m', mass.grund.m);
    this.setAuto('mb_grund_mom', mass.grund.mom);
    this.setAuto('mb_start_m', mass.start.m);
    this.setAuto('mb_start_mom', mass.start.mom);

    // ---- warnings ----
    // A hand-written ETA wins the display, so it must also drive the warning.
    const etaForCheck = this.manual.has('eta') ? parseHMSafe(val('eta')) : etaMin ?? parseHMSafe(val('eta'));
    const checks = safetyWarnings({
      etaMin: etaForCheck,
      ss: val('ss'),
      totalTimeMin: Number.isFinite(totalTimeEff) ? Math.round(totalTimeEff) : null,
      safeTime: val('safe_time'),
      incompleteRoute: route.incomplete,
    });

    // core's message travels along as the fallback for a code we cannot translate
    const ui: UiWarning[] = checks.map(w => ({ level: w.level, code: w.code, fallback: w.message }));
    this.warnings = [...unflyable, ...ui];
    this.red = this.warnings.some(w => w.level === 'red');
    for (const w of ui) {
      const ref: WarnRef = { code: w.code, ...(w.fallback !== undefined ? { fallback: w.fallback } : {}) };
      switch (w.code) {
        case 'route-incomplete':
          this.flag('eta', 'warn2', ref);
          this.flag('total_time', 'warn2', ref);
          break;
        case 'eta-next-day':
        case 'eta-after-sunset':
          this.flag('eta', 'warn', ref);
          break;
        case 'eta-near-sunset':
          this.flag('eta', 'warn2', ref);
          break;
        case 'exceeds-safe-time':
          this.flag('total_time', 'warn', ref);
          break;
        default:
          break;
      }
    }

    this.notify();
  }
}

/**
 * What makes two briefings the same answer: the same stations, observed and
 * forecast for the same times, with the same fallbacks behind the silent ones.
 * When it was FETCHED is deliberately not in it — that is the whole point (see
 * `setWx`), and how fresh the answer is is a property of the session, not of the
 * document.
 */
function wxKey(s: WxStatus | null | undefined): string {
  if (!s) return '';
  const stations = s.stations
    .map(st => [st.id, st.obs_time ?? '', st.taf_valid_to ?? ''].join(':'))
    .join(';');
  const nearby = (s.nearby ?? [])
    .map(n => [n.for, n.id, n.dist_nm, n.obs_time ?? '', n.taf_valid_to ?? ''].join(':'))
    .join(';');
  return (s.source ?? '') + FIELD_SEP + stations + FIELD_SEP + nearby;
}

/**
 * What makes two winds-aloft fills the same answer: the same model, the same
 * forecast hour(s) and the same legs at the same altitudes. `fetched_at` and the
 * time that was ASKED for are left out — the second moves by a minute on every
 * re-read of an unchanged plan (see `setWinds`).
 */
function windsKey(s: WindsStatus | null | undefined): string {
  if (!s) return '';
  return [
    s.model ?? '', s.hour ?? '', (s.hours ?? []).join(','), s.source ?? '', s.fp ?? '',
  ].join(FIELD_SEP);
}

/**
 * What makes two terrain summaries the same answer — everything except when it
 * was read. See `setTerrain` for why the timestamp is left out of the comparison.
 */
function terrainKey(s: TerrainStatus | null | undefined): string {
  if (!s) return '';
  const legs = s.legs
    .map(l => [l.leg, l.max_terrain_ft ?? '', l.msa_ft ?? '', l.msa_rule ?? ''].join(':'))
    .join(';');
  return String(s.corridor_nm ?? '') + FIELD_SEP + (s.source ?? '') + FIELD_SEP + legs;
}

/** A number the calculator produced, or null when it produced none. */
const finite = (v: number): number | null => (Number.isFinite(v) ? v : null);

/** core's parseHM, with NaN mapped to null for the checks. Never re-implement the regex here. */
function parseHMSafe(s: string): number | null {
  const m = parseHM(s);
  return Number.isFinite(m) ? m : null;
}

/* ---------- localStorage ---------- */

const LS_PREFIX = 'avia.plan.';
export const LOCAL_KEY = LS_PREFIX + 'local';
export const planKey = (id: string): string => LS_PREFIX + id;

interface StoredDoc {
  v?: Record<string, string>;
  m?: string[];
  /** the route document (see `route.ts`); absent in anything written before the map existed */
  r?: unknown;
  /** `plans.meta` — the derived-tag sets; absent in anything written before they existed */
  x?: unknown;
  version?: number;
  title?: string;
}

export function saveLocal(key: string, doc: PlanDoc, extra?: { version?: number; title?: string }): void {
  try {
    const payload: StoredDoc = {
      v: doc.values,
      m: doc.manual,
      ...(doc.route ? { r: doc.route } : {}),
      ...(doc.meta ? { x: doc.meta } : {}),
      ...extra,
    };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    /* private mode / quota — the API copy (if signed in) is the durable one */
  }
}

export function readLocal(key: string): { doc: PlanDoc; version?: number; title?: string } | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as StoredDoc;
    if (!d || typeof d !== 'object' || !d.v) return null;
    const values: Record<string, string> = {};
    for (const [k, v] of Object.entries(d.v)) values[k] = String(v ?? '');
    return {
      doc: { values, manual: Array.isArray(d.m) ? d.m : [], route: parseRoute(d.r), meta: parsePlanMeta(d.x) },
      ...(typeof d.version === 'number' ? { version: d.version } : {}),
      ...(typeof d.title === 'string' ? { title: d.title } : {}),
    };
  } catch {
    return null;
  }
}

export function clearLocal(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
